import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, ShieldCheck } from "lucide-react";
import { apiGet, apiPatch } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PersonalProviders } from "./personal-providers";
export function AssistantPreferences() {
  const fr = useI18n().language === "fr";
  const client = useQueryClient();
  const settings = useQuery({
    queryKey: ["assistant-settings"],
    queryFn: () =>
      apiGet<{
        settings: {
          version: number;
          timezone: string;
          personalProjectId: string | null;
          discordConversationId: string | null;
          quietHours: { start: string; end: string } | null;
          monthlyTokenBudget: number | null;
          dailyCapacityMinutes: number | null;
        };
      }>("/ai/assistant/settings"),
  });
  const projects = useQuery({
    queryKey: ["assistant-projects"],
    queryFn: () =>
      apiGet<{ projects: { id: string; name: string; role: string }[] }>(
        "/ai/assistant/projects",
      ),
  });
  const chats = useQuery({
    queryKey: ["ai-conversations", ""],
    queryFn: () =>
      apiGet<{ conversations: { id: string; title: string }[] }>(
        "/ai/assistant/conversations",
      ),
  });
  const [timezone, setTimezone] = useState("");
  const [personal, setPersonal] = useState("");
  const [discord, setDiscord] = useState("");
  const [budget, setBudget] = useState("");
  const [capacity, setCapacity] = useState("");
  const [quiet, setQuiet] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  useEffect(() => {
    const value = settings.data?.settings;
    if (!value) return;
    setTimezone(value.timezone);
    setPersonal(value.personalProjectId ?? "");
    setDiscord(value.discordConversationId ?? "");
    setBudget(value.monthlyTokenBudget ? String(value.monthlyTokenBudget) : "");
    setCapacity(
      value.dailyCapacityMinutes ? String(value.dailyCapacityMinutes) : "",
    );
    setQuiet(!!value.quietHours);
    setStart(value.quietHours?.start ?? "");
    setEnd(value.quietHours?.end ?? "");
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () =>
      apiPatch("/ai/assistant/settings", {
        expectedVersion: settings.data!.settings.version,
        timezone,
        personalProjectId: personal || null,
        discordConversationId: discord || null,
        monthlyTokenBudget: budget ? Number(budget) : null,
        dailyCapacityMinutes: capacity ? Number(capacity) : null,
        quietHours: quiet ? { start, end } : null,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["assistant-settings"] });
      void client.invalidateQueries({ queryKey: ["assistant-calendar"] });
      void client.invalidateQueries({ queryKey: ["assistant-budget"] });
    },
  });
  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold">
          {fr ? "Préférences personnelles" : "Personal preferences"}
        </h2>
        <p className="mt-1 text-sm text-foreground-muted">
          {fr
            ? "Vos réglages sont privés. Les permissions des projets restent la limite de chaque action."
            : "Your settings are private. Project permissions remain the limit for every action."}
        </p>
      </div>
      <form
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (!save.isPending) save.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-2 text-sm">
            <span>{fr ? "Fuseau horaire" : "Timezone"}</span>
            <Input
              required
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
              placeholder="Europe/Paris"
            />
          </label>
          <label className="space-y-2 text-sm">
            <span>
              {fr
                ? "Capacité quotidienne (minutes, facultatif)"
                : "Daily capacity (minutes, optional)"}
            </span>
            <Input
              type="number"
              min="1"
              max="1440"
              value={capacity}
              onChange={(event) => setCapacity(event.target.value)}
            />
          </label>
        </div>
        <label className="block space-y-2 text-sm">
          <span>
            {fr
              ? "Projet utilisé pour les tâches personnelles"
              : "Project for personal tasks"}
          </span>
          <select
            className="w-full rounded-lg border border-white/10 bg-surface px-3 py-2"
            value={personal}
            onChange={(event) => setPersonal(event.target.value)}
          >
            <option value="">
              {fr ? "Demander à chaque fois" : "Ask each time"}
            </option>
            {projects.data?.projects
              .filter((project) => project.role !== "VIEWER")
              .map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
          </select>
        </label>
        <label className="block space-y-2 text-sm">
          <span>
            {fr
              ? "Conversation reliée à Discord"
              : "Conversation connected to Discord"}
          </span>
          <select
            className="w-full rounded-lg border border-white/10 bg-surface px-3 py-2"
            value={discord}
            onChange={(event) => setDiscord(event.target.value)}
          >
            <option value="">
              {fr
                ? "Aucune conversation sélectionnée"
                : "No conversation selected"}
            </option>
            {chats.data?.conversations.map((chat) => (
              <option key={chat.id} value={chat.id}>
                {chat.title}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-2 text-sm" id="automation-token-budget">
          <span>
            {fr
              ? "Budget mensuel partagé des automatisations IA (tokens)"
              : "Shared monthly AI automation budget (tokens)"}
          </span>
          <Input
            id="monthly-token-budget"
            type="number"
            min="1"
            max="2000000000"
            value={budget}
            onChange={(event) => setBudget(event.target.value)}
          />
          <span className="block text-xs text-foreground-muted">
            {fr
              ? "Entrées et sorties comptabilisées; cache inclus dans les entrées. Les rappels déterministes ne consomment aucun token. Aucun montant n’est défini automatiquement."
              : "Input and output tokens counted; cached tokens are included in input. Deterministic reminders consume no tokens. No limit is filled automatically."}
          </span>
        </label>
        <div className="rounded-xl border border-white/10 p-4 space-y-3">
          <label className="flex items-center justify-between text-sm">
            <span>{fr ? "Heures de silence" : "Quiet hours"}</span>
            <Switch checked={quiet} onCheckedChange={setQuiet} />
          </label>
          {quiet && (
            <div className="flex flex-wrap gap-3">
              <label className="text-sm">
                {fr ? "Début" : "Start"}
                <Input
                  type="time"
                  required
                  value={start}
                  onChange={(event) => setStart(event.target.value)}
                />
              </label>
              <label className="text-sm">
                {fr ? "Fin" : "End"}
                <Input
                  type="time"
                  required
                  value={end}
                  onChange={(event) => setEnd(event.target.value)}
                />
              </label>
            </div>
          )}
        </div>
        {save.error && (
          <p role="alert" className="text-sm text-destructive">
            {save.error.message}
          </p>
        )}
        <Button
          type="submit"
          disabled={!settings.data || save.isPending}
          className="gap-2"
        >
          {save.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {fr ? "Enregistrer" : "Save"}
        </Button>
        {save.isSuccess && (
          <span role="status" className="ml-3 text-sm text-emerald-300">
            {fr ? "Enregistré" : "Saved"}
          </span>
        )}
      </form>
      <div className="border-t border-white/10 pt-6">
        <h3 className="mb-4 flex items-center gap-2 font-medium">
          <ShieldCheck className="h-4 w-4 text-primary" />
          {fr ? "Mes fournisseurs IA" : "My AI providers"}
        </h3>
        <PersonalProviders />
      </div>
    </div>
  );
}
